import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import NProgress from 'nprogress';
import { ToastContainer } from 'react-toastify';
import { SiteDataProvider } from './context/site-data-context';
import { HttpClientCtxProvider } from './hooks/http/use-http-client';
import { Footer } from './components/common/footer/footer';
import { CookieBanner } from './components/common/cookie-banner/cookie-banner';
import { MATOMO_SITE_ID, MATOMO_URL } from './constants/environment/environment';
import { hasAnalyticsConsent, initMatomo, trackMatomoPageView } from './analytics/matomo';

import HomePage from './pages/index';
import AziendaPage from './pages/azienda/index';
import AreeMercatoPage from './pages/aree-mercato/index';
import ContattiPage from './pages/contatti/index';
import SegnalazioniPage from './pages/segnalazioni/index';
import InnovationCenterPage from './pages/innovation-center/index';
// import LavoraConNoiPage from './pages/lavora-con-noi/index'; Commented temporarly
import NewsEventiPage from './pages/news-eventi/index';
import StorieSuccessoPage from './pages/storie-successo/index';
import SettoriIndustriaPage from './pages/settori-industria/index';
import CyberSecurityPage from './pages/cyber-security/index';
import ServiziSoluzioniPage from './pages/servizi-soluzioni/[idservice]/index';
import ServiziSoluzioniDetailPage from './pages/servizi-soluzioni/[idservice]/category/[idcategory]/detail/[iddetail]/index';
import TecnologiePage from './pages/tecnologie/[idtechnology]/index';
import TecnologieDetailPage from './pages/tecnologie/[idtechnology]/category/[idcategory]/detail/[iddetail]/index';
import StoriaDetailPage from './pages/[slide]/detail/[idstoria]/index';

function NavigationProgress() {
  const location = useLocation();
  useEffect(() => {
    NProgress.configure({ showSpinner: false });
  }, []);
  useEffect(() => {
    NProgress.start();
    const id = window.setTimeout(() => NProgress.done(), 280);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search, location.hash]);
  return null;
}

function MatomoBridge() {
  const location = useLocation();

  useEffect(() => {
    const onGranted = () => {
      initMatomo({ url: MATOMO_URL, siteId: MATOMO_SITE_ID });
      trackMatomoPageView();
    };
    if (hasAnalyticsConsent()) {
      initMatomo({ url: MATOMO_URL, siteId: MATOMO_SITE_ID });
    }
    window.addEventListener('analytics-consent-granted', onGranted);
    return () => window.removeEventListener('analytics-consent-granted', onGranted);
  }, []);

  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    initMatomo({ url: MATOMO_URL, siteId: MATOMO_SITE_ID });
    trackMatomoPageView();
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function AppChrome({ children }) {
  const [openPrivacyModal, setOpenPrivacyModal] = useState(false);

  return (
    <HttpClientCtxProvider>
      <NavigationProgress />
      <MatomoBridge />
      {children}
      <Footer setOpenPrivacyModal={setOpenPrivacyModal} />
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar
        pauseOnVisibilityChange
        closeOnClick
        pauseOnHover
      />
      <CookieBanner setOpenPrivacyModal={setOpenPrivacyModal} openPrivacyModal={openPrivacyModal} />
    </HttpClientCtxProvider>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <SiteDataProvider>
        <BrowserRouter>
          <AppChrome>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/azienda" element={<AziendaPage />} />
              <Route path="/aree-mercato" element={<AreeMercatoPage />} />
              <Route path="/contatti" element={<ContattiPage />} />
              <Route path="/segnalazioni" element={<SegnalazioniPage />} />
              <Route path="/innovation-center" element={<InnovationCenterPage />} />
              {/* <Route path="/lavora-con-noi" element={<LavoraConNoiPage />} /> */}
              <Route path="/news-eventi" element={<NewsEventiPage />} />
              <Route path="/storie-successo" element={<StorieSuccessoPage />} />
              <Route path="/settori-industria" element={<SettoriIndustriaPage />} />
              <Route path="/cyber-security" element={<CyberSecurityPage />} />
              <Route path="/servizi-soluzioni/:idservice" element={<ServiziSoluzioniPage />} />
              <Route
                path="/servizi-soluzioni/:idservice/category/:idcategory/detail/:iddetail"
                element={<ServiziSoluzioniDetailPage />}
              />
              <Route path="/tecnologie/:idtechnology" element={<TecnologiePage />} />
              <Route
                path="/tecnologie/:idtechnology/category/:idcategory/detail/:iddetail"
                element={<TecnologieDetailPage />}
              />
              <Route path="/:slide/detail/:idstoria" element={<StoriaDetailPage />} />
            </Routes>
          </AppChrome>
        </BrowserRouter>
      </SiteDataProvider>
    </HelmetProvider>
  );
}
